import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';
import { useCreateDailyRecord } from '@/hooks/api/cbs';
import { useQueryClient } from '@tanstack/react-query';
import { FeedType } from '@/types/cbs';
import { FeedTypePicker, KgCounter, DailyEstimateCard } from '@/components/cbs';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { getAuthenticatedClient } from '@/services/client';
import { SimpleMetric } from '@/components/cbs/SimpleMetric';
import { UtensilsCrossed, Check, Home, Camera, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useInlineCamera } from '@/hooks/useInlineCamera';

export const FeedLogScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { activeBatch } = useCBSBatchStore();
  const createRecord = useCreateDailyRecord();
  const queryClient = useQueryClient();
  const { isSimpleMode } = useSimpleMode();

  const [feedKg, setFeedKg] = useState(0);
  const [feedType, setFeedType] = useState<FeedType>('grower');
  const [showSuccess, setShowSuccess] = useState(false);
  const inlineCamera = useInlineCamera('feed_delivery');

  // Calculate batch day from placement date
  const batchDay = activeBatch
    ? Math.max(1, Math.floor((Date.now() - new Date(activeBatch.placement_date).getTime()) / 86400000) + 1)
    : 0;

  const suggestedFeedType: FeedType = activeBatch
    ? batchDay <= 10
      ? 'starter'
      : batchDay <= 24
        ? 'grower'
        : 'finisher'
    : 'grower';

  useEffect(() => {
    setFeedType(suggestedFeedType);
  }, [suggestedFeedType]);

  if (!activeBatch) {
    return (
      <Screen topBarProps={{ title: isSimpleMode ? '\uD83C\uDF3E' : t('cbs.feed.title', { defaultValue: '\uD83C\uDF3E Feed Log' }), showBack: true, showMapPin: false, showSettings: false }} showBottomNav={false}>
        <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
          <UtensilsCrossed className="w-16 h-16 text-muted-foreground" />
          <p className="text-lg text-muted-foreground text-center">{t('cbs.batch.noBatch')}</p>
          <Button onClick={() => navigate('/')} className="gap-2">
            <Home className="w-4 h-4" />
            {t('common.goHome')}
          </Button>
        </div>
      </Screen>
    );
  }

  const liveBirds = (activeBatch.current_bird_count ?? activeBatch.doc_count) - (activeBatch.cumulative_mortality ?? 0);

  const handleSubmit = async () => {
    if (feedKg === 0) {
      toast({ title: t('cbs.feed.enterAmount'), variant: 'destructive' });
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    try {
      const client = getAuthenticatedClient();
      
      const { data: existingRecord } = await client
        .from('daily_records')
        .select('id')
        .eq('batch_id', activeBatch.id)
        .eq('record_date', today)
        .maybeSingle();

      if (existingRecord) {
        const { error } = await client
          .from('daily_records')
          .update({
            feed_consumed_kg: feedKg,
            feed_type: feedType,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingRecord.id);

        if (error) throw error;

        // Keep home-screen ring + related views in sync with the direct UPDATE.
        // The createRecord path invalidates these in onSettled; the UPDATE
        // path bypasses the mutation hook, so we invalidate manually.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecords', activeBatch.id] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'batch', 'active'] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecord', 'today'] }),
          queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] }),
        ]);

        toast({ title: t("cbs.dailyRecord.dayLogged", { defaultValue: "Day logged!" }), description: t("cbs.feed.savedDesc", { defaultValue: "Feed record updated" }) });
      } else {
        await createRecord.mutateAsync({
          batch_id: activeBatch.id,
          user_id: activeBatch.user_id,
          country_id: activeBatch.country_id,
          record_date: today,
          batch_day: batchDay,
          mortality_count: 0,
          mortality_cause: null,
          culling_count: null,
          feed_consumed_kg: feedKg,
          feed_type: feedType,
          water_consumed_liters: null,
          temperature_min_c: null,
          temperature_max_c: null,
          humidity_pct: null,
          weight_sample_count: null,
          weight_sample_avg_g: null,
          production_stage: null,
          is_synced: false,
          recorded_at: new Date().toISOString(),
          recorded_by: null,
        });
      }

      setShowSuccess(true);
      if ('vibrate' in navigator) navigator.vibrate([50, 50, 100]);
      setTimeout(() => navigate('/'), 1500);
    } catch {
      toast({ title: t("cbs.feed.saveFailed", { defaultValue: "Failed to save" }), description: t("common.tryAgain"), variant: 'destructive' });
    }
  };

  return (
    <Screen
      topBarProps={{
        title: isSimpleMode ? '🌾' : t('cbs.feed.title', { defaultValue: '🌾 Feed Log' }),
        showBack: true,
        showMapPin: false,
        showSettings: false,
        onBack: () => navigate('/'),
      }}
      showBottomNav={false}
    >
      <div className="p-4 space-y-6">
        {isSimpleMode && (
          <SimpleMetric emoji="📅" value={`Day ${activeBatch ? Math.min(batchDay, activeBatch.planned_cycle_days) : batchDay}`} size="md" color="info" />
        )}

        <FeedTypePicker
          selectedType={feedType}
          suggestedType={suggestedFeedType}
          onSelect={setFeedType}
        />

        <KgCounter value={feedKg} onChange={setFeedKg} />

        {isSimpleMode && feedKg > 0 && (
          <SimpleMetric emoji="🌾" value={feedKg} unit="kg" size="lg" color="success" />
        )}

        <DailyEstimateCard liveBirds={liveBirds} />

        {/* Photo Capture */}
        <Card className={cn("p-4", !inlineCamera.photoTaken && "border-warning")}>
          <input
            ref={inlineCamera.fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={inlineCamera.handleFileSelected}
          />
          <Button
            variant={inlineCamera.photoTaken ? "default" : "outline"}
            className={cn("w-full gap-3", isSimpleMode ? "h-20 text-2xl" : "h-16 text-lg")}
            onClick={inlineCamera.openCamera}
            disabled={inlineCamera.isUploading}
          >
            {inlineCamera.isUploading ? (
              <><Loader2 className="w-6 h-6 animate-spin" />{!isSimpleMode && t("common.uploading", { defaultValue: "Uploading..." })}</>
            ) : inlineCamera.photoTaken ? (
              <>{isSimpleMode ? '✅ 📸' : <><Check className="w-6 h-6" />{t("camera.photoTaken", { defaultValue: "Photo taken" })}</>}</>
            ) : (
              <>{isSimpleMode ? '📸' : <><Camera className="w-6 h-6" />{t("cbs.feed.takePhoto", { defaultValue: "Take photo of feed" })}</>}</>
            )}
          </Button>
          {!inlineCamera.photoTaken && !isSimpleMode && (
            <p className="text-xs text-warning text-center mt-2">{t("cbs.feed.photoRequired", { defaultValue: "Photo required before saving" })}</p>
          )}
        </Card>

        <Button
          className={cn("w-full gap-2", isSimpleMode ? "h-20 text-2xl" : "h-16 text-xl")}
          onClick={handleSubmit}
          disabled={createRecord.isPending || feedKg === 0 || !inlineCamera.photoTaken}
        >
          {createRecord.isPending ? (
            <><span className="animate-spin">⏳</span> {!isSimpleMode && t('common.saving')}</>
          ) : (
            <>
              {isSimpleMode ? '✅' : <Check className="w-6 h-6" />}
              {!isSimpleMode && t('common.save')}
            </>
          )}
        </Button>
      </div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="text-8xl"
            >
              🎉
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 text-xl font-semibold text-foreground"
            >
              {t("common.saved", { defaultValue: "Saved!" })}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </Screen>
  );
};

export default FeedLogScreen;
